import { Injectable, signal } from '@angular/core';

import { ILanguageItem } from './interfaces';

@Injectable({
    providedIn: 'root'
})
export class LanguageSelectorService {
    currentLanguage$ = signal<string | null>(null);

    public get currentLanguage(): string | null {
        return this.currentLanguage$();
    }
    public set currentLanguage(value: string | null) {
        this.currentLanguage$.set(value);
    }


    getLanguage(eventLanguages: ILanguageItem[], storedLanguage: string, preferredLanguage: string | null): string {
        const eventLanguageFromStorage = eventLanguages.find(x => x.id === storedLanguage);
        const preferredEventLanguage = eventLanguages.find(x => x.id === preferredLanguage);
        // The order of precedence is (if found in event languages):
        // - Language from storage
        // - Preferred language
        // - The first event language
        return eventLanguageFromStorage?.id || preferredEventLanguage?.id || eventLanguages[0].id;
    }
}

export type SupportedLanguageIsoId = 'en-US' | 'en-GB' | 'de-DE' | 'nl-NL' | 'fr-FR' | 'es-ES' | 'ca-ES' | 'da-DK' |
     'it-IT' | 'pt-PT' | 'pt-BR' | 'sv-SE' | 'cs-CZ' | 'el-GR' | 'he-IL' | 'hr-HR' | 'hu-HU' | 'ja-JP' | 'ko-KR' |
     'nn-NO' | 'pl-PL' | 'ro-RO' | 'ru-RU' | 'fi-FI' | 'zh-CN' | 'vi-VN' | 'sk-SK' | 'bg-BG' | 'tr-TR' | 'ur-PK' |
     'uk-UA' | 'hi-IN' | 'th-TH' | 'ms-MY' | 'id-ID' | 'fil-PH' | 'sr-Latn' | 'ar-AE';

export const LANGUAGE_DEPENDENCY_RECORD = {
    'ar-AE': {
        momentLocaleImportFn: () => import('moment/locale/ar' as any),
    },
    'bg-BG': {
        momentLocaleImportFn: () => import('moment/locale/bg' as any),
    },
    'ca-ES': {
        momentLocaleImportFn: () => import('moment/locale/ca' as any),
    },
    'cs-CZ': {
        momentLocaleImportFn: () => import('moment/locale/cs' as any),
    },
    'da-DK': {
        momentLocaleImportFn: () => import('moment/locale/da' as any),
    },
    'de-DE': {
        momentLocaleImportFn: () => import('moment/locale/de' as any),
    },
    'el-GR': {
        momentLocaleImportFn: () => import('moment/locale/el' as any),
    },
    'en-GB': {
        momentLocaleImportFn: () => import('moment/locale/en-gb' as any),
    },
    'en-US': {
        momentLocaleImportFn: async () => {},
    },
    'es-ES': {
        momentLocaleImportFn: () => import('moment/locale/es' as any),
    },
    'fi-FI': {
        momentLocaleImportFn: () => import('moment/locale/fi' as any),
    },
    'fil-PH': {
        momentLocaleImportFn: () => import('moment/locale/fil' as any),
    },
    'fr-FR': {
        momentLocaleImportFn: () => import('moment/locale/fr' as any),
    },
    'he-IL': {
        momentLocaleImportFn: () => import('moment/locale/he' as any),
    },
    'hi-IN': {
        momentLocaleImportFn: () => import('moment/locale/hi' as any),
    },
    'hr-HR': {
        momentLocaleImportFn: () => import('moment/locale/hr' as any),
    },
    'hu-HU': {
        momentLocaleImportFn: () => import('moment/locale/hu' as any),
    },
    'id-ID': {
        momentLocaleImportFn: () => import('moment/locale/id' as any),
    },
    'it-IT': {
        momentLocaleImportFn: () => import('moment/locale/it' as any),
    },
    'ja-JP': {
        momentLocaleImportFn: () => import('moment/locale/ja' as any),
    },
    'ko-KR': {
        momentLocaleImportFn: () => import('moment/locale/ko' as any),
    },
    'ms-MY': {
        momentLocaleImportFn: () => import('moment/locale/ms-my' as any),
    },
    'nl-NL': {
        momentLocaleImportFn: () => import('moment/locale/nl' as any),
    },
    'nn-NO': {
        momentLocaleImportFn: () => import('moment/locale/nn' as any),
    },
    'pl-PL': {
        momentLocaleImportFn: () => import('moment/locale/pl' as any),
    },
    'pt-BR': {
        momentLocaleImportFn: () => import('moment/locale/pt-br' as any),
    },
    'pt-PT': {
        momentLocaleImportFn: () => import('moment/locale/pt' as any),
    },
    'ro-RO': {
        momentLocaleImportFn: () => import('moment/locale/ro' as any),
    },
    'ru-RU': {
        momentLocaleImportFn: () => import('moment/locale/ru' as any),
    },
    'sk-SK': {
        momentLocaleImportFn: () => import('moment/locale/sk' as any),
    },
    'sr-Latn': {
        momentLocaleImportFn: () => import('moment/locale/sr' as any),
    },
    'sv-SE': {
        momentLocaleImportFn: () => import('moment/locale/sv' as any),
    },
    'th-TH': {
        momentLocaleImportFn: () => import('moment/locale/th' as any),
    },
    'tr-TR': {
        momentLocaleImportFn: () => import('moment/locale/tr' as any),
    },
    'uk-UA': {
        momentLocaleImportFn: () => import('moment/locale/uk' as any),
    },
    'ur-PK': {
        momentLocaleImportFn: () => import('moment/locale/ur' as any),
    },
    'vi-VN': {
        momentLocaleImportFn: () => import('moment/locale/vi' as any),
    },
    'zh-CN': {
        momentLocaleImportFn: () => import('moment/locale/zh-cn' as any),
    },
} satisfies {
    [languageId in SupportedLanguageIsoId]: {
        momentLocaleImportFn: () => Promise<void>
    }
};
